import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, Loader2, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";
import { useAuth, userRoles } from "@/context/AuthContext";
import { Crest } from "@/components/Brand";

export default function VerifyPage() {
  const { ticket } = useParams();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const load = () => api.get(`/public/verify/${ticket}`).then((r) => setV(r.data));
  useEffect(() => { load(); }, [ticket]); // eslint-disable-line react-hooks/exhaustive-deps

  const board = async (ticket_no) => {
    try {
      await api.post(`/bookings/${v.code}/board`, { ticket_no });
      toast.success("Passenger marked as boarded");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!v) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  const staff = userRoles(user).some((r) => ["admin", "teller", "station_master"].includes(r));
  return (
    <div className="hero-bg min-h-full">
      <div className="mx-auto max-w-lg px-4 py-10">
        <div className="mb-4 flex items-center justify-center gap-3" data-testid="verify-seal">
          <Crest className="h-12 w-12" />
          <div className="leading-tight"><div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#182678]">GPRTU official ticket verification</div><div className="text-xs text-slate-500">Developed by Nacasky Co. Ltd</div></div>
        </div>
        <div className={`rise relative overflow-hidden rounded-3xl border-2 p-6 text-center ${v.valid ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="verify-result">
          <Crest variant="navy" className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 opacity-[0.06]" />
          {v.valid ? <ShieldCheck className="mx-auto h-14 w-14 text-emerald-600" /> : <ShieldX className="mx-auto h-14 w-14 text-rose-600" />}
          <div className={`mt-2 font-display text-3xl font-extrabold ${v.valid ? "text-emerald-700" : "text-rose-700"}`} data-testid="verify-status">{v.valid ? "VALID TICKET" : "NOT VALID"}</div>
          <div className="text-sm font-semibold text-slate-700">{v.reason}</div>
        </div>
        {v.trip && (
          <div className="card-soft mt-4 p-5">
            <div className="font-display text-lg font-extrabold">{v.trip.origin_city} → {v.trip.destination_city}</div>
            <div className="text-sm text-slate-500">{fmtDate(v.trip.departure)} · {fmtTime(v.trip.departure)} · Bus {v.trip.bus_number}</div>
            <div className="mt-4 divide-y divide-slate-100">
              {v.passengers.map((p) => (
                <div key={p.ticket_no} className="flex items-center justify-between gap-3 py-2.5" data-testid={`verify-passenger-${p.seat}`}>
                  <div><div className="font-semibold">Seat {p.seat} · {p.name}</div><div className="font-mono text-xs text-slate-500">{p.ticket_no} · {p.phone}</div></div>
                  {p.boarded ? <span className="chip bg-sky-100 text-sky-700"><UserCheck className="h-3.5 w-3.5" />Boarded</span>
                    : staff && v.valid && <Button size="sm" className="rounded-full bg-[#0052FF] hover:bg-[#0041CC]" onClick={() => board(p.ticket_no)} data-testid={`board-button-${p.seat}`}>Board</Button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
